import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Search as SearchIcon, Filter, Users, X } from 'lucide-react';
import { User, SearchFilters, UserRole } from '../types';
import { getMockUsers } from '../data/mockData';

const Search = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<SearchFilters>({});

  useEffect(() => {
    document.title = 'Search - FemSportNet';
    
    const fetchUsers = async () => {
      try {
        setIsLoading(true);
        const data = await getMockUsers();
        setUsers(data);
        setFilteredUsers(data);
      } catch (error) {
        console.error('Error fetching users:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUsers();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [searchTerm, filters, users]);

  const applyFilters = () => {
    let result = [...users];
    
    // Apply search term
    if (searchTerm) {
      result = result.filter(user => 
        user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (user.sport && user.sport.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (user.location && user.location.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }
    
    // Apply role filter
    if (filters.role) {
      result = result.filter(user => user.role === filters.role);
    }
    
    // Apply sport filter
    if (filters.sport) {
      result = result.filter(user => 
        user.sport && user.sport.toLowerCase().includes(filters.sport!.toLowerCase())
      );
    }
    
    // Apply location filter
    if (filters.location) {
      result = result.filter(user => 
        user.location && user.location.toLowerCase().includes(filters.location!.toLowerCase())
      );
    }
    
    // Apply verified filter
    if (filters.verified !== undefined) {
      result = result.filter(user => user.verified === filters.verified);
    }
    
    setFilteredUsers(result);
  };

  const handleFilterChange = (key: keyof SearchFilters, value: any) => {
    setFilters({ ...filters, [key]: value });
  };

  const clearFilters = () => {
    setFilters({});
    setSearchTerm('');
  };

  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  const roleOptions: { value: UserRole; label: string }[] = [
    { value: 'athlete', label: 'Athlete' },
    { value: 'coach', label: 'Coach' },
    { value: 'club', label: 'Club' },
    { value: 'federation', label: 'Federation' },
    { value: 'journalist', label: 'Journalist' },
    { value: 'fan', label: 'Fan' }
  ];

  return (
    <div className="bg-gray-50 dark:bg-gray-900 min-h-screen py-8 transition-colors duration-200">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
              Discover Athletes & Professionals
            </h1>
            <p className="text-gray-600 dark:text-gray-300 max-w-3xl">
              Connect with athletes, coaches, and sports professionals from around the world.
            </p>
          </div>

          {/* Search and Filter Bar */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-8 transition-colors duration-200">
            <div className="flex flex-col md:flex-row md:items-center md:space-x-4">
              <div className="relative flex-grow mb-4 md:mb-0">
                <input
                  type="text"
                  placeholder="Search by name, sport, or location"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full py-2 pl-10 pr-4 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-500 transition-colors duration-200"
                />
                <SearchIcon className="absolute left-3 top-2.5 text-gray-400" size={18} />
              </div>
              
              <button
                onClick={toggleFilters}
                className="flex items-center px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors duration-200"
              >
                <Filter size={18} className="mr-2" />
                <span>Filters</span>
              </button>
            </div>
            
            {/* Filters */}
            {showFilters && (
              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Role
                  </label>
                  <select
                    value={filters.role || ''}
                    onChange={(e) => handleFilterChange('role', e.target.value || undefined)}
                    className="w-full py-2 px-3 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-500 transition-colors duration-200"
                  >
                    <option value="">All Roles</option>
                    {roleOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Sport
                  </label>
                  <input
                    type="text"
                    placeholder="Filter by sport"
                    value={filters.sport || ''}
                    onChange={(e) => handleFilterChange('sport', e.target.value || undefined)}
                    className="w-full py-2 px-3 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-500 transition-colors duration-200"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Location
                  </label>
                  <input
                    type="text"
                    placeholder="Filter by location"
                    value={filters.location || ''}
                    onChange={(e) => handleFilterChange('location', e.target.value || undefined)}
                    className="w-full py-2 px-3 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-500 transition-colors duration-200"
                  />
                </div>
                
                <div className="md:col-span-2">
                  <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
                    <input
                      type="checkbox"
                      checked={filters.verified || false}
                      onChange={(e) => handleFilterChange('verified', e.target.checked || undefined)}
                      className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                    />
                    <span className="ml-2">Verified accounts only</span>
                  </label>
                </div>
                
                <div className="flex justify-end">
                  <button
                    onClick={clearFilters}
                    className="flex items-center text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors duration-200"
                  >
                    <X size={16} className="mr-1" />
                    <span>Clear filters</span>
                  </button>
                </div>
              </div>
            )}
            
            {/* Active Filters */}
            {Object.keys(filters).length > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 flex flex-wrap gap-2">
                {filters.role && (
                  <div className="bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 px-3 py-1 rounded-full text-sm flex items-center">
                    Role: {filters.role}
                    <button
                      onClick={() => handleFilterChange('role', undefined)}
                      className="ml-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                    >
                      <X size={14} />
                    </button>
                  </div>
                )}
                
                {filters.sport && (
                  <div className="bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 px-3 py-1 rounded-full text-sm flex items-center">
                    Sport: {filters.sport}
                    <button
                      onClick={() => handleFilterChange('sport', undefined)}
                      className="ml-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                    >
                      <X size={14} />
                    </button>
                  </div>
                )}
                
                {filters.location && (
                  <div className="bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 px-3 py-1 rounded-full text-sm flex items-center">
                    Location: {filters.location}
                    <button
                      onClick={() => handleFilterChange('location', undefined)}
                      className="ml-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                    >
                      <X size={14} />
                    </button>
                  </div>
                )}
                
                {filters.verified && (
                  <div className="bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 px-3 py-1 rounded-full text-sm flex items-center">
                    Verified only
                    <button
                      onClick={() => handleFilterChange('verified', undefined)}
                      className="ml-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                    >
                      <X size={14} />
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
          
          {/* Results */}
          {isLoading ? (
            <div className="flex justify-center my-12">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500"></div>
            </div>
          ) : (
            <>
              <div className="mb-4 flex items-center justify-between">
                <p className="text-gray-600 dark:text-gray-300">
                  {filteredUsers.length} {filteredUsers.length === 1 ? 'result' : 'results'} found
                </p>
              </div>
              
              {filteredUsers.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredUsers.map(user => (
                    <Link
                      key={user.id}
                      to={`/profile/${user.id}`}
                      className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden transition-all duration-200 hover:shadow-md transform hover:-translate-y-1"
                    >
                      <div className="h-32 bg-green-500 relative">
                        {user.role === 'athlete' && user.sport && (
                          <div className="absolute bottom-2 left-2 bg-white dark:bg-gray-800 rounded-full px-2 py-1 text-xs font-medium text-gray-900 dark:text-white">
                            {user.sport}
                          </div>
                        )}
                        {user.verified && (
                          <div className="absolute bottom-2 right-2 bg-blue-500 rounded-full w-6 h-6 flex items-center justify-center">
                            <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                          </div>
                        )}
                      </div>
                      
                      <div className="px-4 pt-4 pb-6">
                        <div className="flex items-start">
                          <div className="flex-shrink-0 -mt-12 mr-3">
                            <div className="w-16 h-16 rounded-full border-4 border-white dark:border-gray-800 overflow-hidden">
                              <img 
                                src={user.avatar} 
                                alt={user.name} 
                                className="w-full h-full object-cover"
                              />
                            </div>
                          </div>
                          <div>
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                              {user.name}
                            </h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400 capitalize">
                              {user.role} {user.location && `· ${user.location}`}
                            </p>
                          </div>
                        </div>
                        
                        {user.bio && (
                          <p className="mt-3 text-gray-600 dark:text-gray-300 text-sm line-clamp-2">
                            {user.bio}
                          </p>
                        )}
                        
                        <div className="mt-4 flex items-center text-sm text-gray-500 dark:text-gray-400">
                          <Users size={16} className="mr-1" />
                          <span>{user.stats.followers} followers</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 text-center">
                  <div className="flex justify-center mb-4">
                    <SearchIcon size={48} className="text-gray-400" />
                  </div>
                  <h3 className="text-xl font-medium text-gray-900 dark:text-white mb-2">
                    No results found
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 mb-4">
                    Try adjusting your search or filters to find what you're looking for
                  </p>
                  <button
                    onClick={clearFilters}
                    className="bg-green-500 hover:bg-green-600 text-white font-medium px-4 py-2 rounded-md transition-colors duration-200"
                  >
                    Clear all filters
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Search;